import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  OnDestroy,
  OnInit,
  SecurityContext,
  signal
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Auth,
  CheckValidSessionUseCase,
  GetLoggedUserUsecase,
  GetOrganizationsUseCase,
  LogOutUseCase,
  SaveUserAccountUsecase
} from '@core/core';
import { Failure, Success } from '@sdk/sdk';

import { DomSanitizer } from '@angular/platform-browser';
import { Organization } from '@core/auth/domain/organization';
import { CookieService } from '@core/auth/infrastructure/cookie.service';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrganizationRedirectHelper } from './organization-redirect-helper';

@Component({
  selector: 'app-organization-list',
  templateUrl: './organization-list.component.html',
  styleUrls: ['./organization-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrganizationListComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();
  isError = false;
  isLoading = false;
  isRedirecting = signal<Organization | undefined>(undefined);
  isLoggingOut = false;
  organizations = signal<Organization[]>([]);
  searchText = signal<string>('');
  loggedUser?: Auth;
  search$ = new Subject<string>();
  licenseError = false;
  duplicatedDeviceError = false;
  invalidOrganizationError = false;

  organizationList = computed(() =>
    this.organizations().filter(organization =>
      organization.name.toLowerCase().includes(this.searchText().toLowerCase())
    )
  );
  showSearch = computed(() => this.organizations().length > 5);

  constructor(
    private _checkSessionUseCase: CheckValidSessionUseCase,
    private _getOrganizationsUsecase: GetOrganizationsUseCase,
    private _saveUserAccountUsecase: SaveUserAccountUsecase,
    private _getLoggedUserUsecase: GetLoggedUserUsecase,
    private _logOutUsecase: LogOutUseCase,
    private _router: Router,
    private _route: ActivatedRoute,
    private _organizationRedirect: OrganizationRedirectHelper,
    private _changeDetector: ChangeDetectorRef,
    private _sanitizer: DomSanitizer,
    private _cookieService: CookieService
  ) {}

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    this._checkError();
    const hasSession = await this._checkHasSession();
    if (hasSession) {
      await this._loadOrganizationList();
      this._handleRedirect();
    }
    const loggedUserResult = await this._getLoggedUserUsecase.execute();
    if (loggedUserResult instanceof Success) {
      this.loggedUser = loggedUserResult.value;
    }
    this._initSearch();
    this._changeDetector.markForCheck();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  async logOut(): Promise<void> {
    this.isLoggingOut = true;
    await this._logOutUsecase.execute();
    this._router.navigate(['/login']);
  }

  onSearchChanged(searchText: string): void {
    this.search$.next(searchText);
  }

  redirectingTo(organization: Organization): boolean {
    return (
      this.isRedirecting()?.url.host == organization.url.host && this.isRedirecting()?.id == organization.id
    );
  }

  async organizationClick(organization: Organization, event: MouseEvent): Promise<void> {
    // If not left or middle button, do nothing
    if (event.button > 1) {
      return;
    }
    if (organization.ssoIsMandatory) {
      const redirectLink = this._getRedirectLink(organization.identityProviderName);
      this._cleanCognitoCookies();
      window.location.href = redirectLink;
      return;
    }
    if (this.loggedUser) {
      await this._saveUserAccountUsecase.execute(
        this.loggedUser.email,
        this.loggedUser.name,
        organization,
        this.loggedUser.avatar
      );
    }

    // Set organization ID cookie for sytex.io domain before redirecting
    this._setOrganizationCookie(organization.id);

    const url = `${organization.url}o/${organization.id}/`;
    if (event.button == 0) {
      this.isRedirecting.set(organization);
      window.location.href = url;
      return;
    }
    window.open(url, '_blank');
  }

  closeDialog(): void {
    this.licenseError = false;
    this.invalidOrganizationError = false;
    this._changeDetector.markForCheck();
  }

  private _initSearch(): void {
    this.search$
      .pipe(distinctUntilChanged(), debounceTime(50), takeUntil(this._destroy$))
      .subscribe(searchText => {
        this.searchText.set(searchText);
      });
  }

  private _getRedirectLink(organization: string): string {
    const redirectLink = environment.sso.cognito_identity_provider_url;
    const redirectLinkWithClientId = redirectLink.replace('USERCLIENTID', environment.cognito.client_id);
    const redirectLinkWithOrganization = redirectLinkWithClientId.replace('USERINPUT', organization);
    return redirectLinkWithOrganization;
  }

  private _setOrganizationCookie(organizationId: string): void {
    // Set organization ID cookie for all sytex.io subdomains
    // Cookie expires in 30 days
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 30);

    const domain = environment.cognito.cookie_domain;
    this._cookieService.setCookieWithDate('Organization', organizationId, expirationDate, domain);
  }

  private async _loadOrganizationList(): Promise<void> {
    this.isLoading = true;
    const result = await this._getOrganizationsUsecase.execute();
    this.isLoading = false;
    if (result instanceof Failure) {
      this.isError = true;
      return;
    }
    this.organizations.set(result.value);
  }

  private _cleanCognitoCookies(): void {
    const domain = environment.cognito.cookie_domain;
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      if (name.startsWith('CognitoIdentityServiceProvider')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}; Secure; SameSite=Lax`;
      }
    });
  }

  private _handleRedirect(): void {
    if (this.duplicatedDeviceError || this.licenseError || this.invalidOrganizationError) {
      return;
    }
    const redirectTo = this._route.snapshot.queryParamMap.get('r');
    if (!redirectTo) {
      if (this.organizations.length != 1) {
        return;
      }
      if (this.organizations()[0].ssoIsMandatory) {
        this._cleanCognitoCookies();
        this._setOrganizationCookie(this.organizations()[0].id);
        window.location.href = this._getRedirectLink(this.organizations()[0].identityProviderName);
        return;
      }
      // Set organization ID cookie before redirecting to single organization
      this._setOrganizationCookie(this.organizations()[0].id);
      window.location.href = this._organizationRedirect.getOrganizationUrl(this.organizations()[0]);
      return;
    }

    const sanitizedRedirectTo = this._sanitizer.sanitize(SecurityContext.URL, redirectTo);

    if (!sanitizedRedirectTo || sanitizedRedirectTo.startsWith('unsafe:')) {
      return;
    }

    const redirectUrl = this._organizationRedirect.getRedirectUrl(this.organizations(), sanitizedRedirectTo);

    if (redirectUrl) {
      const organization = this._organizationRedirect.getOrganizationFromUrl(
        this.organizations(),
        redirectUrl
      );
      this.isRedirecting.set(organization);

      // Set organization ID cookie before redirecting
      if (organization) {
        this._setOrganizationCookie(organization.id);
      }

      window.location.href = redirectUrl;
    }
  }

  private async _checkHasSession(): Promise<boolean> {
    const result = await this._checkSessionUseCase.execute();
    if (result instanceof Failure) {
      this._router.navigate(['/login']);
      return false;
    }
    return true;
  }
  private _checkError(): void {
    const error = this._route.snapshot.queryParamMap.get('error');
    if (error === 'NO_LICENSE') {
      this.licenseError = true;
    } else if (error === 'DUPLICATED_DEVICE') {
      this.duplicatedDeviceError = true;
    } else if (error === 'AUTH_METHOD_MISMATCH') {
      this.invalidOrganizationError = true;
    }
  }
}
